/**
 * Add-note form contents with local state to avoid re-rendering the whole
 * Medical Record tab on every keystroke (fixes input lag).
 */
import { Button, Group, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useCallback, useState } from 'react';
import { CHART_NOTE_STANDALONE_TEMPLATES } from '../data/chartNoteTemplates';
import { toEpisodeSelectOptions, type EpisodePickerItem } from '../data/episodeSelectOptions';

export interface ChartNoteFormSubmit {
  form: Record<string, string>;
  templateId: string;
  /** Episode of care to add the chart note to (required). */
  episodeId: string;
}

export interface SoapFormSubmit {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

export interface AdminNoteFormSubmit {
  title: string;
  content: string;
  /** Optional episode of care context for the admin note. */
  episodeId?: string;
}

export type EpisodeOption = EpisodePickerItem;

interface AddChartNoteFormContentProps {
  onCancel: () => void;
  onSave: (data: ChartNoteFormSubmit) => void;
  /** Active episodes of care (shown under "Active" group). */
  activeEpisodes: EpisodeOption[];
  /** Finished episodes (follow-up / finished) shown under "Finished" group. */
  inactiveEpisodes: EpisodeOption[];
  /** Pre-select this episode when opening the form (e.g. from episode filter). */
  defaultEpisodeId?: string;
}

export function AddChartNoteFormContent({ onCancel, onSave, activeEpisodes, inactiveEpisodes, defaultEpisodeId }: AddChartNoteFormContentProps): JSX.Element {
  const template = CHART_NOTE_STANDALONE_TEMPLATES[0];
  const templateId = template?.id ?? 'general';
  const [form, setForm] = useState<Record<string, string>>({});
  const firstEpisodeId = defaultEpisodeId ?? activeEpisodes[0]?.id ?? inactiveEpisodes[0]?.id ?? '';
  const [episodeId, setEpisodeId] = useState<string>(firstEpisodeId);

  const handleSubmit = useCallback(() => {
    if (!episodeId) return;
    onSave({ form, templateId, episodeId });
  }, [form, templateId, episodeId, onSave]);

  const canSave = template && episodeId
    ? !template.fields.some((f) => f.required && !(form[f.id] ?? '').trim())
    : false;

  if (!template) {
    return (
      <Stack gap="md">
        <Text size="sm" c="gray.6">No chart note template available.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>Cancel</Button>
        </Group>
      </Stack>
    );
  }

  const episodesCount = activeEpisodes.length + inactiveEpisodes.length;
  if (episodesCount === 0) {
    return (
      <Stack gap="md">
        <Text size="sm" c="gray.6">No episodes of care. Add an episode of care for this client before adding a chart note.</Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>Cancel</Button>
        </Group>
      </Stack>
    );
  }

  const episodeSelectData = [
    ...(activeEpisodes.length > 0
      ? [{ group: 'Active', items: toEpisodeSelectOptions(activeEpisodes) }]
      : []),
    ...(inactiveEpisodes.length > 0
      ? [{ group: 'Finished', items: toEpisodeSelectOptions(inactiveEpisodes) }]
      : []),
  ];

  return (
    <Stack gap="md">
      <Text size="sm" c="gray.6">
        Add a chart note not tied to an appointment. Select the episode of care this note belongs to.
      </Text>
      <Select
        label="Episode of care"
        placeholder="Select episode..."
        data={episodeSelectData}
        value={episodeId || null}
        onChange={(v) => v && setEpisodeId(v)}
        required
      />
      <Stack gap="sm">
        {template.fields.map((field) =>
          field.multiline ? (
            <Textarea
              key={field.id}
              label={field.label}
              placeholder={field.placeholder}
              value={form[field.id] ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [field.id]: e.currentTarget.value }))}
              minRows={3}
              required={field.required}
            />
          ) : (
            <TextInput
              key={field.id}
              label={field.label}
              placeholder={field.placeholder}
              value={form[field.id] ?? ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [field.id]: e.currentTarget.value }))}
              required={field.required}
            />
          )
        )}
      </Stack>
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!canSave}>
          Save note
        </Button>
      </Group>
    </Stack>
  );
}

interface AddRiskAssessmentFormContentProps {
  onCancel: () => void;
  onSave: (data: SoapFormSubmit) => void;
}

export function AddRiskAssessmentFormContent({ onCancel, onSave }: AddRiskAssessmentFormContentProps): JSX.Element {
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');

  const handleSubmit = useCallback(() => {
    onSave({ subjective, objective, assessment, plan });
  }, [subjective, objective, assessment, plan, onSave]);

  return (
    <Stack gap="md">
      <Text size="sm" c="gray.6">
        Document a risk assessment. Include presenting concerns, risk and protective factors, clinical assessment, and recommendations.
      </Text>
      <Textarea
        label="Presenting concerns / Triggers"
        placeholder="What prompted this assessment? Presenting concerns or triggers..."
        value={subjective}
        onChange={(e) => setSubjective(e.currentTarget.value)}
        minRows={2}
      />
      <Textarea
        label="Risk factors and protective factors"
        placeholder="Identify risk factors and protective factors..."
        value={objective}
        onChange={(e) => setObjective(e.currentTarget.value)}
        minRows={2}
      />
      <Textarea
        label="Clinical assessment and level of risk"
        placeholder="Clinical assessment, level of risk, and rationale..."
        value={assessment}
        onChange={(e) => setAssessment(e.currentTarget.value)}
        minRows={2}
        required
      />
      <Textarea
        label="Recommendations and follow-up"
        placeholder="Recommendations, safety plan updates, follow-up..."
        value={plan}
        onChange={(e) => setPlan(e.currentTarget.value)}
        minRows={2}
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!assessment.trim()}>
          Save risk assessment
        </Button>
      </Group>
    </Stack>
  );
}

interface AddSafetyPlanFormContentProps {
  onCancel: () => void;
  onSave: (data: SoapFormSubmit) => void;
}

export function AddSafetyPlanFormContent({ onCancel, onSave }: AddSafetyPlanFormContentProps): JSX.Element {
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');

  const handleSubmit = useCallback(() => {
    onSave({ subjective, objective, assessment, plan });
  }, [subjective, objective, assessment, plan, onSave]);

  return (
    <Stack gap="md">
      <Text size="sm" c="gray.6">
        Document a safety plan. Include warning signs, coping strategies, safe environment, and crisis contacts.
      </Text>
      <Textarea
        label="Warning signs"
        placeholder="Warning signs that a crisis may be developing..."
        value={subjective}
        onChange={(e) => setSubjective(e.currentTarget.value)}
        minRows={2}
      />
      <Textarea
        label="Coping strategies and supports"
        placeholder="Internal coping strategies and people/places that can help..."
        value={objective}
        onChange={(e) => setObjective(e.currentTarget.value)}
        minRows={2}
      />
      <Textarea
        label="Safe environment and means restriction"
        placeholder="Ways to make the environment safe; means restriction..."
        value={assessment}
        onChange={(e) => setAssessment(e.currentTarget.value)}
        minRows={2}
      />
      <Textarea
        label="Crisis contacts and next steps"
        placeholder="Professional and personal crisis contacts; when to use emergency services..."
        value={plan}
        onChange={(e) => setPlan(e.currentTarget.value)}
        minRows={2}
        required
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!plan.trim()}>
          Save safety plan
        </Button>
      </Group>
    </Stack>
  );
}

interface AddAdminNoteFormContentProps {
  onCancel: () => void;
  onSave: (data: AdminNoteFormSubmit) => void;
  activeEpisodes: EpisodeOption[];
  inactiveEpisodes: EpisodeOption[];
  /** Pre-select this episode when opening the form (e.g. from episode filter). */
  defaultEpisodeId?: string;
}

export function AddAdminNoteFormContent({
  onCancel,
  onSave,
  activeEpisodes,
  inactiveEpisodes,
  defaultEpisodeId,
}: AddAdminNoteFormContentProps): JSX.Element {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const defaultEpisode = defaultEpisodeId ?? activeEpisodes[0]?.id ?? inactiveEpisodes[0]?.id ?? '';
  const [episodeId, setEpisodeId] = useState<string | null>(defaultEpisode || null);

  const handleSubmit = useCallback(() => {
    onSave({
      title: title.trim(),
      content: content.trim(),
      ...(episodeId ? { episodeId } : {}),
    });
  }, [title, content, episodeId, onSave]);

  const episodeSelectData = [
    ...(activeEpisodes.length > 0
      ? [{ group: 'Active', items: toEpisodeSelectOptions(activeEpisodes) }]
      : []),
    ...(inactiveEpisodes.length > 0
      ? [{ group: 'Finished', items: toEpisodeSelectOptions(inactiveEpisodes) }]
      : []),
  ];

  return (
    <Stack gap="md">
      <Text size="sm" c="gray.6">
        Add an admin note for internal documentation, reminders, or communication.
      </Text>
      {episodeSelectData.length > 0 && (
        <Select
          label="Episode of care"
          placeholder="Optional — select episode..."
          data={episodeSelectData}
          value={episodeId}
          onChange={setEpisodeId}
          clearable
        />
      )}
      <TextInput
        label="Title"
        placeholder="e.g. Billing follow-up"
        value={title}
        onChange={(e) => setTitle(e.currentTarget.value)}
        required
      />
      <Textarea
        label="Content"
        placeholder="Enter note content..."
        value={content}
        onChange={(e) => setContent(e.currentTarget.value)}
        minRows={4}
        required
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!title.trim() || !content.trim()}>
          Save admin note
        </Button>
      </Group>
    </Stack>
  );
}
